import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ScrollToTopLink from './ScrollToTopLink';
import LearnMoreModal from './LearnMoreModal';
import logoImage from '../assets/logo.png';

const FooterContainer = styled.footer`
  background-color: white;
  padding: 4rem 0 2rem;
  border-top: 1px solid var(--border-color);
  position: relative;
  z-index: 10;
`;

const FooterContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const FooterLogo = styled.div`
  max-width: 250px;
  
  img {
    width: 180px;
    height: auto;
    margin-bottom: 1.5rem;
  }
  
  p {
    font-size: 0.875rem;
    color: #666;
    line-height: 1.6;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    text-align: center;
    margin: 0 auto;
    
    img {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

const FooterNav = styled.div`
  display: flex;
  gap: 2.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 2rem;
    flex-direction: column;
    width: 100%;
    text-align: center;
  }
`;

const FooterColumn = styled.div`
  min-width: 150px;
  
  @media (max-width: 768px) {
    min-width: 100%;
    margin-bottom: 1.5rem;
  }
`;

const FooterTitle = styled.h4`
  font-size: 1rem;
  margin-bottom: 1.25rem;
  color: var(--secondary-color);
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    font-size: 1.1rem;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const FooterLink = styled.li`
  margin-bottom: 0.75rem;
  
  a {
    color: #666;
    font-size: 0.875rem;
    transition: color 0.3s ease;
    
    &:hover {
      color: var(--primary-color);
    }
  }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
  font-size: 0.75rem;
  color: #888;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const LocationText = styled.p`
  font-size: 0.75rem;
  color: #888;
  margin-top: 1rem;
  text-align: center;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.25rem;
  margin-top: 0.5rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
  
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background-color: #f5f5f5;
    color: #555;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: var(--primary-color);
      color: white;
      transform: translateY(-3px);
    }
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

// Instagram Icon Component
const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor">
    <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
  </svg>
);

// LinkedIn Icon Component
const LinkedInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor">
    <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z" />
  </svg>
);

// Mobile Navigation Component
const MobileNavBar = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    z-index: 100;
    height: 60px;
  }
`;

const MobileNavItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  font-size: 0.7rem;
  color: #333;
  text-decoration: none;
  
  svg {
    margin-bottom: 4px;
    width: 20px;
    height: 20px;
  }
  
  &.highlight {
    color: var(--primary-color);
  }
`;

// Home Icon Component
const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

// Contact Icon Component
const ContactIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

// Start Project Icon Component
const StartProjectIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="16"></line>
    <line x1="8" y1="12" x2="16" y2="12"></line>
  </svg>
);

const Footer: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <FooterContainer>
        <div className="container font--jakarta">
          <FooterContent>
            <FooterLogo>
              <ScrollToTopLink to="/">
                <img src={logoImage} alt="Uptrend Systems - Custom Software Development in Whitby, Ontario" />
              </ScrollToTopLink>
              <p>
                We build custom software that actually makes sense for how humans think and work. No more fighting with clunky systems.
              </p>
            </FooterLogo>

            <FooterNav>
              <FooterColumn>
                <FooterTitle>Company</FooterTitle>
                <FooterLinks>
                  <FooterLink><ScrollToTopLink to="/about">About</ScrollToTopLink></FooterLink>
                  <FooterLink><ScrollToTopLink to="/contact">Contact</ScrollToTopLink></FooterLink>
                  <FooterLink><ScrollToTopLink to="/terms">Terms</ScrollToTopLink></FooterLink>
                  <FooterLink><ScrollToTopLink to="/privacy">Privacy</ScrollToTopLink></FooterLink>
                </FooterLinks>
              </FooterColumn>

              <FooterColumn>
                <FooterTitle>Solutions</FooterTitle>
                <FooterLinks>
                  <FooterLink><ScrollToTopLink to="/solutions/business-portals">Business Portals</ScrollToTopLink></FooterLink>
                  <FooterLink><ScrollToTopLink to="/solutions/system-integrations">System Integrations</ScrollToTopLink></FooterLink>
                  <FooterLink><ScrollToTopLink to="/solutions/workflow-automation">Workflow Automation</ScrollToTopLink></FooterLink>
                  <FooterLink><Link to="/#process">Our Process</Link></FooterLink>
                </FooterLinks>
              </FooterColumn>

              <FooterColumn>
                <FooterTitle>Resources</FooterTitle>
                <FooterLinks>
                  <FooterLink><ScrollToTopLink to="/about">About Us</ScrollToTopLink></FooterLink>
                  <FooterLink><ScrollToTopLink to="/blog">Blog</ScrollToTopLink></FooterLink>
                  <FooterLink><ScrollToTopLink to="/contact">Get In Touch</ScrollToTopLink></FooterLink>
                  <FooterLink><ScrollToTopLink to="/sitemap">Site Map</ScrollToTopLink></FooterLink>
                </FooterLinks>
              </FooterColumn>

              <FooterColumn>
                <FooterTitle>Connect With Us</FooterTitle>
                <FooterLinks>
                  <FooterLink><a href="mailto:info@uptrendsystems.com">info@uptrendsystems.com</a></FooterLink>
                  <FooterLink><a href="tel:+1416-646-0414">+1 (416) 646-0414</a></FooterLink>
                  <FooterLink>
                    <SocialLinks>
                      <a href="http://instagram.com/uptrendsystems" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram">
                        <InstagramIcon />
                      </a>
                      <a href="https://www.linkedin.com/company/uptrend-systems/?originalSubdomain=ca" target="_blank" rel="noopener noreferrer" aria-label="Connect with us on LinkedIn">
                        <LinkedInIcon />
                      </a>
                    </SocialLinks>
                  </FooterLink>
                </FooterLinks>
              </FooterColumn>
            </FooterNav>
          </FooterContent>

          <LocationText>
            Serving businesses in Whitby, Durham Region, and across Ontario with custom software development and system integration solutions.
          </LocationText>

          <FooterBottom>
            <div>© {new Date().getFullYear()} Uptrend Systems. All Rights Reserved</div>
            <div>
              <ScrollToTopLink to="/terms" style={{ color: '#888', marginRight: '15px', fontSize: '0.75rem' }}>Terms</ScrollToTopLink>
              <ScrollToTopLink to="/privacy" style={{ color: '#888', marginRight: '15px', fontSize: '0.75rem' }}>Privacy</ScrollToTopLink>
              <ScrollToTopLink to="/contact" style={{ color: '#888', fontSize: '0.75rem' }}>Contact</ScrollToTopLink>
            </div>
          </FooterBottom>
        </div>
      </FooterContainer>

      <MobileNavBar>
        <ScrollToTopLink to="/" style={{ textDecoration: 'none', flex: 1 }}>
          <MobileNavItem>
            <HomeIcon />
            <span>Home</span>
          </MobileNavItem>
        </ScrollToTopLink>
        <ScrollToTopLink to="/contact" style={{ textDecoration: 'none', flex: 1 }}>
          <MobileNavItem>
            <ContactIcon />
            <span>Contact</span>
          </MobileNavItem>
        </ScrollToTopLink>
        <div style={{ textDecoration: 'none', flex: 1 }} onClick={openModal}>
          <MobileNavItem className="highlight">
            <StartProjectIcon />
            <span>Get Started</span>
          </MobileNavItem>
        </div>
      </MobileNavBar>

      <LearnMoreModal
        isOpen={isModalOpen}
        onClose={closeModal}
        serviceName="Custom Software Solutions"
      />
    </>
  );
};

export default Footer; 